/**
 * 388 (74.15's style; the owner chose all four from lane E's sheet, design_sheets/mountain_style.html): the battle planet's
 * mountains drawn by the ground's shape, not by whole facets — three drawing pieces chained onto the planet mesh, the
 * landforms' far strips and the fine patch (the fourth, the cliffs joined into walls, is generation: lane C's, in the
 * landforms pass).
 *
 * - **Rock by the slope**: rock at each pixel, from a grade of 0.8 to whole at the walk's 1.2, its edge broken by noise. On
 *   the mesh the slope is each corner's (its facets' mean, half way to their steepest), blended across the facet; the far
 *   strips and the patch read their own normal. The facets today's colouring turned to rock by their slope take their band's
 *   colour back (a crater's, a scorch's or the fog's colour on one stays), so a face reads as one rock, not whole facets.
 * - **Strata**: height bands on the rock, meandered, with seams, toned from the rock and the world's strata palette, faded
 *   before they alias. Bold (7-person beds) on the mesa worlds (the dry ones), fine (3) elsewhere; the Lab's *Strata* row.
 * - **Snow by a snowline**: where the climate (74.12's temperatureAt, per pixel: the latitude, the height, the air) is cold
 *   and the ground stands over its world kind's snowline (the world sheet's elevation share: `SNOWLINE`), shed from faces
 *   past ~40°, a face from the sun holding it lower. The band's own snowcap goes with it. Never on an airless world; the
 *   line keeps an icy world's plains their own colour. The Lab's *Snowline* row (lower, higher, off).
 *
 * Drawn only: nothing in the sim reads it. The action clock at the human scale, the battle planet, as 71.8's look; the Lab's
 * *Mountain style* row (on by default).
 */
import * as THREE from 'three';
import type { PlanetState, PlanetArchetype, PlanetLayerProfile } from '../types';
import { computeLayeredVertexColors, layerProfileFor, strataFor, hexToRgb, facetSlope } from '../core/planetColors';
import { meshWeld, type Weld } from '../core/materialRelief';
import { worldSheetOf } from '../core/worldSheet';
import { CLIMATE, ARCHETYPE_TEMPERATURE } from '../core/climate';
import { airOf } from '../core/air';
import { KINDS_BY_ARCHETYPE } from '../core/landforms';
import type { PlanetMeshData } from './renderer';

export const MOUNTAIN_STYLE = {
  /** rock: a corner's slope `steep` of the way from its facets' mean to their steepest; rock from a grade of `grades[0]` to
   *  whole at the walk's limit (1.2: ground a person scrambles is rock) */
  rock: { steep: 0.5, grades: [0.8, 1.2] as [number, number] },
  /** strata: the bed's thickness (people), the tones' spread, the seams' darkness, the meander's reach (people) and wavelength */
  strata: {
    fine: { period: 3, spread: 0.2, seam: 0.1, wobble: 2, wave: 30 },
    bold: { period: 7, spread: 0.36, seam: 0.16, wobble: 4, wave: 60 },
  },
  /** snow: shed between these slope factors (1 − n·up), a sunward face warmer by `aspect`; the Lab's lower and higher move
   *  the line by `shift`; none under `minAir` of the air's density */
  snow: { shed: [0.2, 0.36] as [number, number], aspect: 0.08, colour: '#eef2f7', shift: 0.15, minAir: 0.05 },
};

/** 388: each world kind's snowline, a share of its height (the world sheet's elevation), from the cold ground's spread over
 *  the Lab's worlds (lab-38, lab-39): snow on the heights, the plains their own — bruma's 100 % cold ground white on 5–27 %,
 *  thalia's on 6–17 %; the dry and the pale worlds a dusting on their summits. */
export const SNOWLINE: Record<PlanetArchetype, number> = {
  thalia: 0.45, viridis: 0.4, murex: 0.5, pelago: 0.4, bruma: 0.45, tephra: 0.4, ochrus: 0.6, sonora: 0.6, pallor: 0.6,
};

export type StrataDial = 'kind' | 'bold' | 'fine' | 'off';
export type SnowlineDial = 'kind' | 'lower' | 'higher' | 'off';
export interface MountainStyleDials { on: boolean; strata: StrataDial; snowline: SnowlineDial }

// ---- the dials: the Lab's rows at its launch, else the URL's (?mstyle=0, ?strata=, ?snowline=) ---------------------------------

let override: MountainStyleDials | null = null;
export function setMountainStyle(d: MountainStyleDials | null): void { override = d; }
export function mountainStyleDials(): MountainStyleDials {
  if (override) return override;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
    const strata = q?.get('strata') ?? '', snowline = q?.get('snowline') ?? '';
    return {
      on: q?.get('mstyle') !== '0',
      strata: (['kind', 'bold', 'fine', 'off'] as const).find(s => s === strata) ?? 'kind',
      snowline: (['kind', 'lower', 'higher', 'off'] as const).find(s => s === snowline) ?? 'kind',
    };
  } catch { return { on: true, strata: 'kind', snowline: 'kind' }; }
}

/** Whether this battle draws the style, and on which planet: the action clock at the human scale, the row on. */
export function mountainStylePlanetIndex(state: { config: { clock?: string } }, battleIndex: number, k: number): number | null {
  return state.config.clock === 'action' && k < 1 && mountainStyleDials().on ? battleIndex : null;
}

/** The strata a world draws: bold on the mesa worlds (the dry ones), fine elsewhere; or the dial's. */
export function strataOf(arch: PlanetArchetype, dial: StrataDial): 'bold' | 'fine' | null {
  if (dial === 'off') return null;
  if (dial !== 'kind') return dial;
  return (KINDS_BY_ARCHETYPE[arch] ?? []).includes('mesa') ? 'bold' : 'fine';
}

/** A world's snowline (an elevation share), or null for none: the dial off, or an airless world. */
export function snowlineOf(arch: PlanetArchetype, airDensity: number, dial: SnowlineDial): number | null {
  if (dial === 'off' || airDensity < MOUNTAIN_STYLE.snow.minAir) return null;
  const line = SNOWLINE[arch] ?? 0.45;
  return dial === 'lower' ? line - MOUNTAIN_STYLE.snow.shift : dial === 'higher' ? line + MOUNTAIN_STYLE.snow.shift : line;
}

/** The profile the mesh's colours take under the style: no facet turned to rock by its slope; with the snow, the band's
 *  snowcap (a top band named snow, cap or ice) its band below's colour. */
export function styleProfile(profile: PlanetLayerProfile, snow: boolean): PlanetLayerProfile {
  const layers = profile.layers.slice(), top = layers.length - 1;
  if (snow && top > 0 && /snow|cap|ice/i.test(layers[top].name)) layers[top] = { ...layers[top], color: layers[top - 1].color };
  return { ...profile, layers, rockSlope: [9, 10] };
}

// ---- the colours: the live colour, where it is the colour as made the style's base -----------------------------------------------

/** The entries whose colour as made differs from the style's base (their facets' slope rock, the snowcap): the index, both colours. */
export interface StyleSubs { idx: Int32Array; made: Float32Array; base: Float32Array }

export function styleSubs(made: Float32Array, base: Float32Array): StyleSubs {
  const list: number[] = [];
  for (let i = 0; i < made.length / 3; i++) {
    if (made[i * 3] !== base[i * 3] || made[i * 3 + 1] !== base[i * 3 + 1] || made[i * 3 + 2] !== base[i * 3 + 2]) list.push(i);
  }
  const idx = Int32Array.from(list), m = new Float32Array(idx.length * 3), b = new Float32Array(idx.length * 3);
  for (let k = 0; k < idx.length; k++) for (let q = 0; q < 3; q++) { m[k * 3 + q] = made[idx[k] * 3 + q]; b[k * 3 + q] = base[idx[k] * 3 + q]; }
  return { idx, made: m, base: b };
}

/** The colours drawn: the live ones, each substituted entry its base where its live colour is still the one it was made with
 *  (a crater's, a scorch's or the fog's colour stays). */
export function styledColours(live: ArrayLike<number>, subs: StyleSubs, out: Float32Array): Float32Array {
  out.set(live);
  for (let k = 0; k < subs.idx.length; k++) {
    const i = subs.idx[k] * 3, j = k * 3;
    if (Math.abs(live[i] - subs.made[j]) < 1e-4 && Math.abs(live[i + 1] - subs.made[j + 1]) < 1e-4 && Math.abs(live[i + 2] - subs.made[j + 2]) < 1e-4) {
      out[i] = subs.base[j]; out[i + 1] = subs.base[j + 1]; out[i + 2] = subs.base[j + 2];
    }
  }
  return out;
}

// ---- the shader ---------------------------------------------------------------------------------------------------------------------

const NOISE = /* glsl */`
float msHash(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
float msNoise(vec3 x) {
  vec3 i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(msHash(i), msHash(i + vec3(1, 0, 0)), f.x), mix(msHash(i + vec3(0, 1, 0)), msHash(i + vec3(1, 1, 0)), f.x), f.y),
             mix(mix(msHash(i + vec3(0, 0, 1)), msHash(i + vec3(1, 0, 1)), f.x), mix(msHash(i + vec3(0, 1, 1)), msHash(i + vec3(1, 1, 1)), f.x), f.y), f.z);
}`;

const FRAG = /* glsl */`
{
  vec3 rel = vMsP - uMsCentre, up = normalize(rel), p = rel / uMsPerson;
  vec3 n = normalize(vMsN);
#ifdef MS_COARSE
  float slope = vMsSlope;   // (the corners' steepest facets, blended across the facet: a face one rock, its edge soft)
#else
  float slope = 1.0 - clamp(dot(n, up), 0.0, 1.0);
#endif
  vec3 c = diffuseColor.rgb;
  // rock by the slope here, its edge broken by a noise a few tens of people across
  float wob = (msNoise(p / 23.0) - 0.5) * 0.14 + (msNoise(p / 5.0) - 0.5) * 0.07;
  float mid = 0.5 * (uMsRockSlope.x + uMsRockSlope.y), soft = 0.25 * (uMsRockSlope.y - uMsRockSlope.x);
  float rk = smoothstep(mid - soft, mid + soft, slope + wob);
  vec3 rock = uMsRock * (0.92 + 0.16 * msNoise(p / 9.0));
#ifdef MS_STRATA
  {
    float h = (length(rel) - uMsR) / uMsPerson;
    h += uMsStrat.w * (msNoise(p / uMsWave) - 0.5) * 2.0 + 0.6 * uMsStrat.w * (msNoise(p / (uMsWave * 0.23)) - 0.5);
    float b = h / uMsStrat.x, fb = fract(b), id = floor(b);
    float pick = msHash(vec3(id, 3.7, 1.3));
    vec3 tone = pick < 0.25 ? uMsStrata[0] : pick < 0.5 ? uMsStrata[1] : pick < 0.75 ? uMsStrata[2] : uMsStrata[3];
    float w = fwidth(b);
    float keep = 1.0 - smoothstep(0.18, 0.5, w);   // (faded to the rock's mean before the bands alias)
    float seam = 1.0 - uMsStrat.z * (1.0 - smoothstep(0.0, max(0.06, w * 1.5), min(fb, 1.0 - fb) - 0.02));
    vec3 banded = mix(rock, tone, min(1.0, uMsStrat.y * 2.5)) * seam;
    rock = mix(rock, banded, keep);
  }
#endif
  c = mix(c, rock, rk);
#ifdef MS_SNOW
  {
    float elev = clamp((length(rel) - uMsElev.x) / uMsElev.y, 0.0, 1.0);
    float sl = clamp(dot(up, uMsAxis), -1.0, 1.0), cl = sqrt(1.0 - sl * sl);
    float t = uMsClimate.x - uMsClimate.y * (1.0 - cl) - uMsClimate.z * elev;
    // a face toward the sun is warmer, one away colder
    vec3 face = n - up * dot(n, up), sunT = uMsSun - up * dot(uMsSun, up);
    if (length(face) > 1e-4 && length(sunT) > 1e-4) t += uMsSnow.z * dot(normalize(face), normalize(sunT)) * smoothstep(0.05, 0.3, slope);
    t += (msNoise(p / 17.0) - 0.5) * 0.04;
    float cold = smoothstep(uMsClimate.w + 0.015, uMsClimate.w - 0.015, t);
    float high = smoothstep(uMsSnow.w - 0.03, uMsSnow.w + 0.03, elev + (msNoise(p / 11.0) - 0.5) * 0.04);
    float snow = cold * high * (1.0 - smoothstep(uMsSnow.x, uMsSnow.y, slope + wob));
    c = mix(c, uMsSnowCol * (0.96 + 0.06 * msNoise(p / 3.0)), snow);
  }
#endif
  diffuseColor.rgb = c;
}`;

/**
 * Chain the style onto a material (`coarse`: the planet mesh — its corners' slope and its styled colours as attributes; else
 * the far strips or the patch, their own normal). The block runs after the colour and before the patch's craterlet marks;
 * chained before 71.8's look, it reads the look's colour too.
 */
function applyStyle(material: THREE.Material, s: MountainStyleState, coarse: boolean): void {
  const before = material.onBeforeCompile?.bind(material);
  const defs = `${coarse ? '#define MS_COARSE\n' : ''}${s.strata ? '#define MS_STRATA\n' : ''}${s.snowline !== null ? '#define MS_SNOW\n' : ''}`;
  material.onBeforeCompile = (shader, renderer) => {
    before?.(shader, renderer);
    Object.assign(shader.uniforms, s.uniforms);
    shader.vertexShader = defs + shader.vertexShader
      .replace('#include <common>', `#include <common>
varying vec3 vMsP;
varying vec3 vMsN;
#ifdef MS_COARSE
attribute vec3 aMsCol;
attribute float aMsSlope;
varying float vMsSlope;
#endif`)
      .replace('#include <color_vertex>', `#include <color_vertex>
#if defined(MS_COARSE) && defined(USE_COLOR)
  vColor.rgb = aMsCol;   // (the band's colour where the facet's was its slope's rock: the rock drawn below)
#endif`)
      .replace('#include <project_vertex>', `#include <project_vertex>
  vMsP = (modelMatrix * vec4(transformed, 1.0)).xyz;
  vMsN = mat3(modelMatrix) * objectNormal;
#ifdef MS_COARSE
  vMsSlope = aMsSlope;
#endif`);
    shader.fragmentShader = defs + shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform vec3 uMsCentre;
uniform float uMsR, uMsPerson;
uniform vec3 uMsRock;
uniform vec2 uMsRockSlope;
uniform vec3 uMsStrata[4];
uniform vec4 uMsStrat;      // period, spread, seam, wobble (people)
uniform float uMsWave;
uniform vec4 uMsClimate;    // base (with the air), pole cooling, height cooling, cold at
uniform vec2 uMsElev;       // the world sheet's radius range: lo, hi − lo
uniform vec3 uMsAxis;
uniform vec3 uMsSun;
uniform vec4 uMsSnow;       // shed from, shed to, aspect, the snowline
uniform vec3 uMsSnowCol;
varying vec3 vMsP;
varying vec3 vMsN;
#ifdef MS_COARSE
varying float vMsSlope;
#endif
${NOISE}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\n${FRAG}`);
  };
  const key = material.customProgramCacheKey?.bind(material);
  material.customProgramCacheKey = () => `${key ? key() : ''}|mstyle-${coarse ? 'c' : 'p'}-${s.strata ?? 'n'}-${s.snowline !== null ? 's' : 'n'}`;
  material.needsUpdate = true;
}

// ---- the state on the battle planet -------------------------------------------------------------------------------------------------

export interface MountainStyleState {
  planetId: number;
  strata: 'bold' | 'fine' | null;
  snowline: number | null;
  uniforms: Record<string, THREE.IUniform>;
  weld: Weld;
  subs: StyleSubs;
  /** the colours drawn (the mesh's aMsCol; the fine patch reads them) */
  aCol: Float32Array;
  aSlope: Float32Array;
  /** milliseconds: the build's (the colours, the weld, the slopes, the world sheet) */
  buildMs: number;
}

const raw = (hex: string) => { const c = hexToRgb(hex); return new THREE.Color().setRGB(c.r, c.g, c.b, THREE.LinearSRGBColorSpace); };

/** Each corner's slope over the weld's vertices `verts`: its facets' mean, `steep` of the way to their steepest. */
function cornerSlopes(w: Weld, planet: PlanetState, verts: Iterable<number>, out: Float32Array): void {
  const bp = planet.basePositions, hm = planet.heightmap as Float32Array, steep = MOUNTAIN_STYLE.rock.steep;
  for (const v of verts) {
    let sum = 0, mx = 0;
    const from = w.facetsAt[v], to = w.facetsAt[v + 1];
    for (let q = from; q < to; q++) { const s = facetSlope(bp, hm, w.facets[q]); sum += s; if (s > mx) mx = s; }
    const mean = to > from ? sum / (to - from) : 0, s = mean + steep * (mx - mean);
    for (let q = from; q < to; q++) {
      const t = w.facets[q], i = t * 3 + (w.vertexOf[t * 3] === v ? 0 : w.vertexOf[t * 3 + 1] === v ? 1 : 2);
      out[i] = s;
    }
  }
}

const states = new Map<number, MountainStyleState>();
export function mountainStyleOf(planetId: number): MountainStyleState | null { return states.get(planetId) ?? null; }

/** Build the style on the battle planet's mesh and its far strips (once a battle; its cost on the state: buildMs). */
export function attachMountainStyle(meshData: PlanetMeshData, planet: PlanetState, opts: { person: number; sun: THREE.Vector3; dials?: MountainStyleDials }): MountainStyleState {
  const t0 = performance.now();
  const dials = opts.dials ?? mountainStyleDials(), arch = planet.archetype as PlanetArchetype;
  const geo = meshData.mesh.geometry as THREE.BufferGeometry, n = planet.heightmap.length;
  const sheet = worldSheetOf(planet), air = airOf(planet).density;
  const strata = strataOf(arch, dials.strata), snowline = snowlineOf(arch, air, dials.snowline);
  // the colours: as made, and the style's base (the bands without the slope's rock, the snowcap the snow's)
  const profile = layerProfileFor(planet);
  const made = computeLayeredVertexColors(planet), base = computeLayeredVertexColors(planet, undefined, { profile: styleProfile(profile, snowline !== null) });
  const subs = styleSubs(made, base);
  const aCol = styledColours((geo.getAttribute('color') as THREE.BufferAttribute).array as Float32Array, subs, new Float32Array(n * 3));
  const w = meshWeld(planet.basePositions), V = w.facetsAt.length - 1, aSlope = new Float32Array(n);
  cornerSlopes(w, planet, Array.from({ length: V }, (_, v) => v), aSlope);
  geo.setAttribute('aMsCol', new THREE.BufferAttribute(aCol, 3));
  geo.setAttribute('aMsSlope', new THREE.BufferAttribute(aSlope, 1));
  // the uniforms: the centre, a person, the rock and its tones, the climate's parts (core/climate.ts temperatureAt, per pixel)
  let lo = Infinity, hi = -Infinity;
  for (let v = 0; v < sheet.radius.length; v++) { lo = Math.min(lo, sheet.radius[v]); hi = Math.max(hi, sheet.radius[v]); }
  const baseT = (ARCHETYPE_TEMPERATURE[arch] ?? 0.55) - CLIMATE.thinAirCooling * (1 - Math.max(0, Math.min(1, air)));
  const rock = raw(profile.rock), st = strataFor(planet).map(l => raw(l.color));
  const lean = (c: THREE.Color, k: number) => rock.clone().lerp(c, k);
  // (the rock's own tones, a lighter and a darker, and two leaning to the world's strata: 63.5's palette)
  const tones = [rock.clone().multiplyScalar(1.4), rock.clone().multiplyScalar(0.68), lean(st[1 % st.length], 0.5).multiplyScalar(1.15), lean(st[st.length - 1], 0.45).multiplyScalar(0.9)];
  const sd = MOUNTAIN_STYLE.strata[strata ?? 'fine'], sn = MOUNTAIN_STYLE.snow;
  const slopeOf = (g: number) => 1 - 1 / Math.sqrt(1 + g * g);
  const uniforms: Record<string, THREE.IUniform> = {
    uMsCentre: { value: meshData.mesh.position }, uMsR: { value: planet.radius }, uMsPerson: { value: opts.person },
    uMsRock: { value: rock }, uMsRockSlope: { value: new THREE.Vector2(slopeOf(MOUNTAIN_STYLE.rock.grades[0]), slopeOf(MOUNTAIN_STYLE.rock.grades[1])) },
    uMsStrata: { value: tones }, uMsStrat: { value: new THREE.Vector4(sd.period, sd.spread, sd.seam, sd.wobble) }, uMsWave: { value: sd.wave },
    uMsClimate: { value: new THREE.Vector4(baseT, CLIMATE.poleCooling, CLIMATE.heightCooling, CLIMATE.coldAt) },
    uMsElev: { value: new THREE.Vector2(lo, Math.max(1e-6, hi - lo)) },
    uMsAxis: { value: new THREE.Vector3(sheet.spinAxis.x, sheet.spinAxis.y, sheet.spinAxis.z) }, uMsSun: { value: opts.sun },
    uMsSnow: { value: new THREE.Vector4(sn.shed[0], sn.shed[1], sn.aspect, snowline ?? 2) }, uMsSnowCol: { value: raw(sn.colour) },
  };
  const s: MountainStyleState = { planetId: planet.id, strata, snowline, uniforms, weld: w, subs, aCol, aSlope, buildMs: 0 };
  applyStyle(meshData.mesh.material as THREE.Material, s, true);
  const far = meshData.mesh.getObjectByName('landformFar') as THREE.Mesh | undefined;
  if (far) applyStyle(far.material as THREE.Material, s, false);
  s.buildMs = performance.now() - t0;
  meshData.mstyle = s;
  states.set(planet.id, s);
  return s;
}

/** The fine patch's material takes its planet's style (its own normals; the planet's uniforms). */
export function applyMountainStyleToPatch(material: THREE.Material, s: MountainStyleState): void { applyStyle(material, s, false); }

export function disposeMountainStyle(meshData: PlanetMeshData): void {
  const s = meshData.mstyle;
  if (!s) return;
  if (states.get(s.planetId) === s) states.delete(s.planetId);
  meshData.mstyle = undefined;
}

/** The mesh's colours changed (a crater's, the fog's): the colours drawn again (a copy and a pass over the substituted entries). */
export function refreshMountainStyleColours(meshData: PlanetMeshData): void {
  const s = meshData.mstyle;
  if (!s) return;
  const geo = meshData.mesh.geometry as THREE.BufferGeometry;
  styledColours((geo.getAttribute('color') as THREE.BufferAttribute).array as Float32Array, s.subs, s.aCol);
  (geo.getAttribute('aMsCol') as THREE.BufferAttribute).needsUpdate = true;
}

/** The mesh's heights changed (`modified`: the entries moved; all when absent): the slope again at every corner of a facet round a moved one. */
export function refreshMountainStyleShape(meshData: PlanetMeshData, planet: PlanetState, modified?: ArrayLike<number>): void {
  const s = meshData.mstyle;
  if (!s) return;
  const w = s.weld, V = w.facetsAt.length - 1;
  let verts: number[];
  if (!modified || modified.length === 0) verts = Array.from({ length: V }, (_, v) => v);
  else {
    const mark = new Uint8Array(V);
    verts = [];
    for (let k = 0; k < modified.length; k++) {
      const v0 = w.vertexOf[modified[k]];
      for (let q = w.facetsAt[v0]; q < w.facetsAt[v0 + 1]; q++) {
        const t = w.facets[q];
        for (let c = 0; c < 3; c++) { const v = w.vertexOf[t * 3 + c]; if (!mark[v]) { mark[v] = 1; verts.push(v); } }
      }
    }
  }
  cornerSlopes(w, planet, verts, s.aSlope);
  (meshData.mesh.geometry.getAttribute('aMsSlope') as THREE.BufferAttribute).needsUpdate = true;
}
